import { useState, useEffect, useRef } from "react";
import api from "../../api/axios"; 
import "./Settings.css";
import { toast } from "react-toastify";
function Subscription() {
  // ✅ 100% PRESERVED STATES
  const [activeTab, setActiveTab] = useState("overview");
  const [planDetails, setPlanDetails] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [canWrite, setCanWrite] = useState(false);
  const hasFetched = useRef(false);

  // ✅ 100% PRESERVED API LOGIC
  const fetchSubscription = async () => {
    try {
      const res = await api.get("/subscription/current");
      setPlanDetails(res.data);
    } catch (err) {
      console.error("Failed to load subscription");
    }
  };

  const fetchPayments = async () => {
    try {
      const res = await api.get("/subscription/payments");
      setPaymentHistory(res.data);
    } catch (err) {
      console.error("Failed to load payments");
    }
  };

  const handleRenew = async () => {
    try {
      await api.post("/subscription/renew");
      fetchSubscription();
      fetchPayments();
    } catch (err) {
      console.error("Renew failed");
    }
  };

  const handlePayment = async (plan) => {
    try {
      const cleanAmount = plan.price.toString().replace(/[$,]/g, "");
      const payload = {
        planId: plan.id,
        planName: plan.name,
        amount: cleanAmount 
      };

      const res = await api.post("/subscription/purchase", payload);
      
     toast.success(res.data.message);
      setShowRenewModal(false);
      
      fetchSubscription();
      fetchPayments();
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Purchase failed";
     toast.error(errorMsg);
      console.error("Payment failed:", errorMsg);
    }
  };

  const downloadInvoice = async (paymentId) => {
    try {
      const response = await api.get(`/subscription/download-invoice/${paymentId}`, {
        responseType: 'blob', 
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${paymentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (err) {
      console.error("Download failed", err);
     toast.error("Could not download invoice. Please try again.");
    }
  };

  const fetchPlans = async () => {
    try {
      const res = await api.get("/plans");
      setPlans(res.data);
    } catch (err) {
      console.error("Failed to load plans");
    }
  };

  const fetchSubscriptionPerms = async () => {
    try {
      const res = await api.get("/user-roles/me");
      const role = res.data;

      if (role.isAdmin) {
        setCanWrite(true);
        return;
      }

      if (role && !role.noRoleDefined) {
        const writeStr = role.write_access || "";
        const bothStr = role.both_access || "";
        const hasWritePerm = writeStr.includes("Subscription") || bothStr.includes("Subscription");
        setCanWrite(hasWritePerm);
      } else {
        setCanWrite(true);
      }
    } catch (err) {
      console.error("Subscription permission check failed");
      setCanWrite(false);
    }
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchSubscriptionPerms();
    fetchSubscription();
    fetchPayments();
    fetchPlans();
  }, []);

  return (
    <div className="sub-wrapper text-start w-100">
      
      {/* ── OVERVIEW TAB ── */}
      {activeTab === "overview" && (
        <div className="sub-overview-fade-in w-100">
          
          {/* 🔥 Slashed gaps from g-4 mb-5 to g-3 mb-4 */}
          <div className="row g-3 mb-4">
            
            {/* 1. CURRENT PLAN CARD */}
            <div className="col-12 col-lg-7">
              {/* 🔥 Slashed padding from p-4 to p-3 */}
              <div className="card shadow-sm border-0 rounded-4 p-3 h-100">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <span className="text-muted small fw-bold text-uppercase tracking-wider" style={{fontSize: "11px"}}>CURRENT PLAN</span>
                    <div className="d-flex align-items-center gap-3 mt-1">
                      {/* 🔥 Scaled down massive font size */}
                      <h2 className="fw-bolder mb-0 text-dark" style={{ fontSize: "2rem" }}>
                        {planDetails?.name?.toUpperCase() || "NO PLAN"}
                      </h2>
                      <span className="badge bg-primary text-white rounded-pill px-3 py-1" style={{fontSize: "11px"}}>
                        {planDetails?.status?.toUpperCase() || "INACTIVE"}
                      </span>
                    </div>
                  </div>
                  <i className="bi bi-patch-check text-primary" style={{ fontSize: "1.75rem" }}></i>
                </div>

                {/* 🔥 Slashed huge top margins from mt-4 pt-4 to mt-3 pt-3 */}
                <div className="d-flex flex-wrap justify-content-between align-items-center mt-3 pt-3 border-top">
                  <div className="d-flex gap-5">
                    <div>
                      <span className="text-muted mb-1 d-block" style={{fontSize: "12px"}}>Last renewal date</span>
                      <span className="fw-bold text-dark" style={{fontSize: "14px"}}>
                        {planDetails?.nextRenewal 
                          ? new Date(new Date(planDetails.nextRenewal).setMonth(new Date(planDetails.nextRenewal).getMonth() - 1)).toISOString().split('T')[0] 
                          : "N/A"}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted mb-1 d-block" style={{fontSize: "12px"}}>Next renewal date</span>
                      <span className="fw-bold text-primary" style={{fontSize: "14px"}}>
                        {planDetails?.nextRenewal ? new Date(planDetails.nextRenewal).toISOString().split('T')[0] : "N/A"}
                      </span>
                    </div>
                  </div>
                  
                  <button 
                    className="btn btn-sm btn-primary px-3 fw-bold mt-2 mt-sm-0 rounded-3"
                    onClick={() => setActiveTab("history")}
                  >
                    View Payment History
                  </button>
                </div>
              </div>
            </div>

            {/* 2. ACTION REQUIRED WARNING CARD */}
            <div className="col-12 col-lg-5">
              {/* 🔥 Slashed padding from p-4 to p-3 */}
              <div className="card border-0 rounded-4 p-3 h-100 sub-alert-card d-flex flex-column">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <i className="bi bi-exclamation-triangle text-warning fs-5"></i>
                  <h6 className="fw-bold mb-0 text-dark">Action Required</h6>
                </div>
                <p className="text-muted mb-3 pe-3 lh-sm" style={{fontSize: "13px"}}>
                  Your credit card expires in 12 days. Please update your payment method to avoid any interruption in service.
                </p>
                <a href="#" className="fw-bold text-dark text-decoration-underline mt-auto d-inline-block" style={{fontSize: "13px"}} onClick={(e) => e.preventDefault()}>
                  Update Card Now
                </a>
              </div>
            </div>
          </div>

          {/* 3. UPGRADE YOUR PLAN SECTION */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="fw-bold mb-0 text-dark" style={{fontSize: "18px"}}>Upgrade Your Plan</h5>
            <span 
              className="text-primary small fw-bold cursor-pointer d-flex align-items-center gap-1"
              onClick={() => setShowDetails(!showDetails)}
            >
              {showDetails ? "See less details" : "See more details"} 
              <i className={`bi bi-chevron-${showDetails ? 'up' : 'down'}`}></i>
            </span>
          </div>

          {/* 🔥 Slashed grid gap from g-4 to g-3 */}
          <div className="row g-3 w-100 m-0">
            {plans.map((plan) => {
              const isCurrent = planDetails?.name === plan.name;
              return (
                <div className="col-12 col-md-6 col-lg-4 px-2" key={plan.id}>
                  {/* 🔥 Slashed padding from p-4 to p-3 */}
                  <div className="card shadow-sm border-0 rounded-4 p-3 h-100 d-flex flex-column sub-plan-card">
                    <div className="d-flex justify-content-between align-items-start mb-1">
                      <h6 className="fw-bold text-dark mb-0">{plan.name}</h6>
                    </div>
                    
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <span className="text-muted" style={{fontSize: "12px"}}>30 day free trial</span>
                      {isCurrent && <span className="badge bg-primary-subtle text-primary rounded-pill px-2 py-1" style={{fontSize: "10px"}}>Current</span>}
                    </div>
                    
                    <h2 className="fw-bolder text-dark mb-0 d-flex align-items-end">
                      ${plan.price}<span className="text-muted fw-normal pb-1 ms-1" style={{fontSize: "14px"}}>/Month</span>
                    </h2>

                    {/* 🔥 CRITICAL FIX: The HR line ONLY appears if details are shown! */}
                 {showDetails && plan.features && (
                      <div className="flex-grow-1 mt-2 sub-features-list">
                        <hr className="my-2 border-light" />
                        <p className="fw-bold text-dark mb-1" style={{fontSize: "11px"}}>Core access</p>
                        {/* 🔥 Changed to lh-sm (small line-height) and reduced font size */}
                     <ul className="list-unstyled text-muted lh-sm mb-0" style={{fontSize: "11px"}}>
  {(typeof plan.features === "string" ? plan.features.split("\n") : plan.features).map((feat, i) => (
    <li key={i} className="d-flex align-items-start gap-2 mb-0">
      <span className="fw-bold" style={{fontSize: "10px"}}>•</span> {feat}
    </li>
  ))}
</ul>
                      </div>
                    )}

                    {/* 🔥 Removes massive pt-3 padding, button tightly hugs the price when details are closed */}
                   <div className="mt-auto pt-2">
                      {canWrite && (
                        <button 
                          className="btn btn-primary btn-sm w-100 fw-bold py-2 rounded-3" 
                          onClick={() => handlePayment(plan)}
                          disabled={isCurrent}
                        >
                          {isCurrent ? "Current Plan" : "Renew"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      )}

      {/* ── PAYMENT HISTORY TAB (Untouched logic) ── */}
      {activeTab === "history" && (
        <div className="sub-history-fade-in w-100">
          <div 
            className="d-flex align-items-center gap-3 mb-4 cursor-pointer text-dark" 
            onClick={() => setActiveTab("overview")}
            style={{ width: "fit-content" }}
          >
            <i className="bi bi-arrow-left fs-5"></i>
            <h5 className="fw-bold mb-0">Payment History</h5>
          </div>

          <div className="card shadow-sm border-0 rounded-4 overflow-hidden w-100">
            <div className="table-responsive w-100">
              <table className="table sub-table align-middle mb-0 w-100">
                <thead>
                  <tr>
                    <th className="ps-4">PAYMENT ID</th>
                    <th>DATE</th>
                    <th>AMOUNT</th>
                    <th>STATUS</th>
                    <th className="text-center pe-4">INVOICE</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentHistory.map((item, index) => (
                    <tr key={index}>
                      <td className="fw-bold text-secondary ps-4">{item.id}</td>
                      <td className="text-muted fw-medium">{new Date(item.date).toLocaleDateString()}</td>
                      <td className="fw-bold text-dark">${item.amount}</td>
                      <td>
                        <span className="badge bg-success-subtle text-success rounded-pill px-3 py-1 fw-semibold">
                          {item.status || "Succeeded"}
                        </span>
                      </td>
                      <td className="text-center pe-4">
                        <button 
                          className="btn btn-sm btn-light border-0 shadow-sm rounded-3 py-1 px-2" 
                          onClick={() => downloadInvoice(item.id)}
                        >
                          <i className="bi bi-download text-muted"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                  {paymentHistory.length === 0 && (
                    <tr>
                      <td colSpan="5" className="text-center text-muted py-4">No payment history found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {paymentHistory.length > 0 && (
              <div className="d-flex justify-content-between align-items-center bg-white p-3 border-top">
                <span className="small text-muted">
                  Showing 1 to {paymentHistory.length} of {paymentHistory.length} results
                </span>
                <div className="d-flex gap-2">
                  <button className="btn btn-sm btn-outline-secondary px-3 py-1 rounded-2 disabled">Previous</button>
                  <button className="btn btn-sm btn-outline-secondary px-3 py-1 rounded-2 disabled">Next</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

export default Subscription;